"use client"

import { useEffect, useMemo, useRef, useState } from "react"
import { useLocation, useNavigate, useParams } from "react-router-dom"

import {
  AlertTriangle,
  Banknote,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  CreditCard,
  MapPin,
  RefreshCw,
  WifiOff,
} from "lucide-react"

import DriverScreen from "../components/DriverScreen.jsx"
import FailureDialog from "../components/FailureDialog.jsx"
import SignaturePad from "../components/SignaturePad.jsx"
import styles from "../css/driver.module.css"
import api from "../../../api"
import LiveMap from "../../../components/LiveMap"
import { formatDistance } from "../../../utils/geo"
import useDriverTelemetry from "../hooks/useDriverTelemetry"
import useOutbox from "../hooks/useOutbox.js"

export default function DriverInTransit() {
  const { jobId } = useParams()
  const navigate = useNavigate()
  const location = useLocation()

  const locked = useMemo(() => {
    const fromState = location?.state && typeof location.state === "object" ? location.state : null
    if (fromState?.originLat != null || fromState?.originLng != null || fromState?.originLabel) {
      return fromState
    }
    // Fall back to the sessionStorage copy so a mid-trip refresh keeps the pinned
    // start, but only when it was stamped for this job.
    try {
      const raw = sessionStorage.getItem("driverLockedStart")
      const parsed = raw ? JSON.parse(raw) : null
      if (!parsed) return null
      return String(parsed.jobId || "") === String(jobId) ? parsed : null
    } catch {
      return null
    }
  }, [location?.state, jobId])

  const routeMode = useMemo(() => {
    const fromState = locked?.routeMode
    if (fromState === "shortest" || fromState === "fastest") return fromState
    const saved = sessionStorage.getItem("driverRouteMode")
    return saved === "shortest" ? "shortest" : "fastest"
  }, [locked?.routeMode])

  const lockedOrigin = useMemo(() => {
    const lat = locked?.originLat
    const lng = locked?.originLng
    const acc = locked?.originAccuracy
    const accNum = acc != null ? Number(acc) : null
    const acceptable = accNum == null || !Number.isFinite(accNum) ? false : accNum <= 150
    if (!acceptable) return null
    if (!Number.isFinite(Number(lat)) || !Number.isFinite(Number(lng))) return null
    return { lat: Number(lat), lng: Number(lng) }
  }, [locked?.originLat, locked?.originLng, locked?.originAccuracy])

  const lockedEtaMinutes = useMemo(() => {
    const v = locked?.etaMinutes
    return Number.isFinite(Number(v)) ? Number(v) : null
  }, [locked?.etaMinutes])

  const [job, setJob] = useState(null)
  const [routeData, setRouteData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const [completing, setCompleting] = useState(false)
  const routeFetchedOnceRef = useRef(false)
  const lastRouteFetchRef = useRef(0)
  const suppressSheetClickRef = useRef(false)

  useEffect(() => {
    if (!jobId) return

    async function loadDetails() {
      try {
        setLoading(true)
        setError(null)
        const detailsRes = await api.get(`/api/driver/deliveries/${jobId}`)
        setJob(detailsRes?.data?.job || null)
      } catch (err) {
        console.error("[DriverInTransit] loadDetails:", err)
        setError(err.message)
      } finally {
        setLoading(false)
      }
    }

    loadDetails()
  }, [jobId])

  // GPS lifecycle, the stability filter, and all upload cadence live in the hook.
  const {
    coords: gpsCoords,
    heading: gpsHeading,
    accuracy: gpsAccuracy,
    speed: gpsSpeed,
    error: gpsError,
    pingFailed,
    distanceToDestination,
    canRouteFromFix,
    flushNow,
  } = useDriverTelemetry({
    jobId,
    destination: routeData?.destination || null,
    enabled: !completing,
  })

  // Route origin, best first: the live fix, then where the driver actually set
  // off from. With neither, the server falls back to the stop's pinned origin —
  // which for the first stop is the branch, not necessarily where they started.
  const routeOriginParams = useMemo(() => {
    if (canRouteFromFix && gpsCoords) {
      return { originLat: gpsCoords.lat, originLng: gpsCoords.lng }
    }
    if (lockedOrigin) {
      return { originLat: lockedOrigin.lat, originLng: lockedOrigin.lng }
    }
    return {}
  }, [canRouteFromFix, gpsCoords, lockedOrigin])

  useEffect(() => {
    if (!jobId) return

    async function loadRoute() {
      try {
        const routeRes = await api.get(`/api/driver/deliveries/${jobId}/route`, {
          params: { mode: routeMode, ...routeOriginParams },
        })
        setRouteData(routeRes?.data || null)
      } catch (err) {
        console.error("[DriverInTransit] loadRoute:", err)
      }
    }

    if (!routeFetchedOnceRef.current) {
      routeFetchedOnceRef.current = true
      loadRoute()
      return
    }

    // Refresh from live GPS periodically. The server caches route calculations,
    // so a refresh that hasn't moved far costs nothing upstream.
    const now = Date.now()
    if (canRouteFromFix && now - lastRouteFetchRef.current >= 30000) {
      lastRouteFetchRef.current = now
      loadRoute()
    }
  }, [jobId, routeMode, canRouteFromFix, routeOriginParams])

  const minutes = useMemo(() => {
    if (lockedEtaMinutes != null) return lockedEtaMinutes
    const secondsRaw = routeData?.route?.durationSeconds
    const seconds = secondsRaw != null ? Number(secondsRaw) : NaN
    if (!Number.isFinite(seconds)) return null
    return Math.max(1, Math.round(seconds / 60))
  }, [routeData, lockedEtaMinutes])

  const [payment, setPayment] = useState("Card")
  const [amountReceived, setAmountReceived] = useState("")

  const [refreshingRoute, setRefreshingRoute] = useState(false)

  const [signature, setSignature] = useState(null)
  const [receivedBy, setReceivedBy] = useState("")

  const [showFailureDialog, setShowFailureDialog] = useState(false)
  const [failing, setFailing] = useState(false)

  const [sheetCollapsed, setSheetCollapsed] = useState(false)

  /**
   * Drag the handle to open or close the sheet.
   *
   * This resolves to the same two states as tapping rather than tracking the
   * sheet's height continuously — a driver pulling at this one-handed in a
   * moving vehicle wants a definite result, not a height they have to land
   * precisely. Tapping still works, so nothing depends on the gesture.
   */
  const handleSheetPointerDown = (event) => {
    const startY = event.clientY
    let settled = false

    const finish = (endY) => {
      if (settled) return
      settled = true
      const delta = endY - startY
      // Short movements fall through to the button's click handler, which
      // toggles — so a tap and a small drag behave the same way.
      if (Math.abs(delta) < 24) return

      // A resolved drag is followed by a click event; without this the toggle
      // would immediately undo the drag.
      suppressSheetClickRef.current = true
      setSheetCollapsed(delta > 0)
    }

    const onPointerUp = (e) => {
      finish(e.clientY)
      window.removeEventListener("pointerup", onPointerUp)
      window.removeEventListener("pointercancel", onPointerCancel)
    }

    const onPointerCancel = () => {
      settled = true
      window.removeEventListener("pointerup", onPointerUp)
      window.removeEventListener("pointercancel", onPointerCancel)
    }

    window.addEventListener("pointerup", onPointerUp)
    window.addEventListener("pointercancel", onPointerCancel)
  }

  const { pending: pendingSyncCount, online, submit } = useOutbox()

  useEffect(() => {
    if (!job?.paymentType) return
    setPayment(job.paymentType === "Cash" ? "Cash" : "Card")
  }, [job?.paymentType])

  const totalNumber = Number(job?.amount ?? 0)
  const total = (Number.isFinite(totalNumber) ? totalNumber : 0).toFixed(2)
  const showPayment = !(job?.paymentType === "MedicalAid" || job?.paymentType === "Credit")

  // Number("abc") is NaN, and every NaN comparison is false — parsing loosely here
  // used to *enable* the Complete button for a garbage cash amount.
  const amountReceivedNumber = useMemo(() => {
    const raw = String(amountReceived).trim()
    if (!raw) return null
    if (!/^\d*\.?\d*$/.test(raw)) return NaN
    const parsed = Number(raw)
    return Number.isFinite(parsed) ? parsed : NaN
  }, [amountReceived])

  const amountInvalid = amountReceivedNumber != null && !Number.isFinite(amountReceivedNumber)
  const amountShort =
    Number.isFinite(amountReceivedNumber) && amountReceivedNumber < Number(total)

  const isCashPayment = showPayment && payment === "Cash"
  // Nothing to collect on a zero-value delivery, so don't force an amount there.
  const cashAmountRequired = isCashPayment && Number(total) > 0

  const paymentDisabled =
    isCashPayment &&
    (amountInvalid || (cashAmountRequired && (amountReceivedNumber == null || amountShort)))

  // Mirrors the server gate: the allowance keeps a driver on a poor signal from
  // being locked out while standing at the door.
  const baseProximityThresholdMeters = 100
  const proximityThresholdMeters =
    baseProximityThresholdMeters + (Number.isFinite(gpsAccuracy) ? gpsAccuracy : 0)

  const withinProximity =
    distanceToDestination != null && distanceToDestination <= proximityThresholdMeters

  const completeDisabled = paymentDisabled || !withinProximity || completing

  // Tell the driver *why* the button is dead instead of leaving them tapping it.
  const completeBlockedReason = useMemo(() => {
    if (completing) return null
    if (!withinProximity) {
      if (distanceToDestination == null) {
        return "Waiting for a GPS fix before you can complete this delivery."
      }
      return `Complete unlocks within ${Math.round(
        proximityThresholdMeters,
      )} m of the destination — you're ${formatDistance(distanceToDestination)} away.`
    }
    if (paymentDisabled) {
      if (amountInvalid) return "Enter a valid cash amount."
      if (amountReceivedNumber == null) return "Enter the cash amount received."
      if (amountShort) return `Cash received is less than the R${total} due.`
    }
    return null
  }, [
    completing,
    withinProximity,
    distanceToDestination,
    proximityThresholdMeters,
    paymentDisabled,
    amountInvalid,
    amountReceivedNumber,
    amountShort,
    total,
  ])

  const changeDue = Number.isFinite(amountReceivedNumber)
    ? (amountReceivedNumber - Number(total)).toFixed(2)
    : ""

  const lineString = routeData?.route?.lineString
  const destination = routeData?.destination
    ? [routeData.destination.lng, routeData.destination.lat]
    : null
  const mapCenter = destination || (lineString?.[0] ? lineString[0] : null)
  const startLabel = locked?.originLabel || routeData?.originLabel || job?.startPoint

  // Clears trip-scoped state once a stop is resolved, so nothing leaks into the
  // next one.
  const clearLockedStart = () => {
    try {
      sessionStorage.removeItem("driverLockedStart")
    } catch {
      // ignore
    }
  }

  const handleComplete = async () => {
    if (!jobId || completing) return
    try {
      setCompleting(true)
      setError(null)

      // Get the tail of the trip stored before the stop stops accepting writes.
      await flushNow()

      // Proof first so that if the completion fails the signature is already
      // safe, and a retry finds it captured rather than asking for it again.
      // (The server does accept a late signature — an offline replay can
      // legitimately arrive after completion — it just never overwrites one.)
      if (signature) {
        await submit({
          url: `/api/driver/deliveries/${jobId}/proof`,
          body: { signature, receivedBy },
          kind: "proof",
          dedupeKey: `proof:${jobId}`,
        })
      }

      const deliveredOnline = await submit({
        url: `/api/driver/deliveries/${jobId}/complete`,
        body: {
          paymentType: payment,
          amountReceived: payment === "Cash" ? amountReceivedNumber : null,
          // The server verifies proximity against this fix, falling back to the
          // AWS tracker only when it's missing or too coarse.
          lat: gpsCoords?.lat ?? null,
          lng: gpsCoords?.lng ?? null,
          accuracy: gpsAccuracy ?? null,
        },
        kind: "complete",
        dedupeKey: `complete:${jobId}`,
      })

      clearLockedStart()

      // Queued offline, the completion is real from the driver's point of view —
      // send them back to the route rather than to a summary the server hasn't
      // recorded yet.
      navigate(deliveredOnline ? `/driver/completed/${jobId}` : "/driver")
    } catch (err) {
      console.error("[DriverInTransit] complete:", err)
      // Previously a "too far" rejection was swallowed with a bare return, so the
      // button just did nothing. Every failure is now shown to the driver.
      setError(err?.message || "Failed to complete delivery")
    } finally {
      setCompleting(false)
    }
  }

  const handleFail = async ({ reason, note }) => {
    if (!jobId || failing) return
    try {
      setFailing(true)
      setError(null)

      await flushNow()

      await submit({
        url: `/api/driver/deliveries/${jobId}/fail`,
        body: { reason, note },
        kind: "fail",
        dedupeKey: `fail:${jobId}`,
      })

      clearLockedStart()
      setShowFailureDialog(false)
      navigate("/driver")
    } catch (err) {
      console.error("[DriverInTransit] fail:", err)
      setError(err?.message || "Failed to record delivery failure")
    } finally {
      setFailing(false)
    }
  }

  const refreshRouteNow = async () => {
    if (!jobId) return
    try {
      setRefreshingRoute(true)
      const routeRes = await api.get(`/api/driver/deliveries/${jobId}/route`, {
        params: { mode: routeMode, ...routeOriginParams },
      })
      setRouteData(routeRes?.data || null)
    } catch (err) {
      console.error("[DriverInTransit] refreshRouteNow:", err)
    } finally {
      setRefreshingRoute(false)
    }
  }

  return (
    <DriverScreen variant="map">
      <div className={styles.mapArea}>
        <LiveMap
          center={mapCenter}
          destination={destination}
          driverPosition={gpsCoords ? [gpsCoords.lng, gpsCoords.lat] : null}
          driverHeading={gpsHeading}
          followDriver={true}
          routeLineString={Array.isArray(lineString) ? lineString : null}
          height="100%"
          zoom={15}
        />

        <div className={styles.mapControls}>
          <button
            className={styles.mapChip}
            disabled={refreshingRoute}
            onClick={refreshRouteNow}
            type="button"
          >
            <RefreshCw size={16} aria-hidden="true" />
            {refreshingRoute ? "Updating…" : "Re-route"}
          </button>
        </div>
      </div>

      <div className={`${styles.sheet} ${sheetCollapsed ? styles.sheetCollapsed : ""}`}>
        {/* A real control, not decoration: drag it or tap it to get the map back. */}
        <button
          type="button"
          className={styles.sheetHandle}
          aria-expanded={!sheetCollapsed}
          aria-controls="trip-sheet-body"
          aria-label={sheetCollapsed ? "Show delivery details" : "Hide delivery details"}
          onClick={() => {
            if (suppressSheetClickRef.current) {
              suppressSheetClickRef.current = false
              return
            }
            setSheetCollapsed((prev) => !prev)
          }}
          onPointerDown={handleSheetPointerDown}
        >
          <span className={styles.sheetGrabber} aria-hidden="true" />
          <span className={styles.sheetHandleHint} aria-hidden="true">
            {sheetCollapsed ? (
              <>
                <ChevronUp size={14} /> Details
              </>
            ) : (
              <>
                <ChevronDown size={14} /> Map
              </>
            )}
          </span>
        </button>

        {loading && <div className={styles.statusMessage}>Loading delivery…</div>}

        {!loading && error && <div className={styles.errorMessage}>{error}</div>}

        {!loading && !error && !job && (
          <div className={styles.statusMessage}>Delivery not found.</div>
        )}

        {!loading && !error && job && (
          <>
            <div className={styles.tripStrip}>
              <div className={styles.tripEta}>
                <span className={styles.tripEtaValue}>{minutes ?? "—"}</span>
                <span className={styles.tripEtaUnit}>min</span>
              </div>
              {distanceToDestination != null && (
                <div className={styles.tripDistance}>
                  {formatDistance(distanceToDestination)} away
                </div>
              )}
            </div>

            <div className={styles.tripDestination}>
              <MapPin size={16} aria-hidden="true" />
              <span>
                {job.destination?.line1 || "—"}
                {job.patient ? ` · ${job.patient}` : ""}
              </span>
            </div>

            {(!online || pendingSyncCount > 0) && (
              <div className={styles.syncBanner}>
                <WifiOff size={16} aria-hidden="true" />
                {!online
                  ? `Offline — work is saved and will sync automatically${
                      pendingSyncCount > 0 ? ` (${pendingSyncCount} queued)` : ""
                    }.`
                  : `Syncing ${pendingSyncCount} queued update${pendingSyncCount === 1 ? "" : "s"}…`}
              </div>
            )}

            {gpsError && <div className={styles.errorMessage}>{gpsError}</div>}

            {pingFailed && (
              <div className={styles.warnMessage}>
                Location tracking isn&apos;t reaching the server — your delivery route may have
                gaps. Check your signal.
              </div>
            )}

            {/* Everything below collapses away so the map can be read while
                driving. ETA, destination and any warnings stay visible above. */}
            <div className={styles.sheetBody} id="trip-sheet-body" hidden={sheetCollapsed}>
            {showPayment && (
              <>
                <div className={styles.amountRow}>
                  <span className={styles.amountLabel}>Amount due</span>
                  <span className={styles.amountValue}>R{total}</span>
                </div>

                <div className={styles.field}>
                  <span className={styles.fieldLabel} id="payment-type-label">
                    Payment type
                  </span>
                  <div
                    className={styles.segmentGroup}
                    role="radiogroup"
                    aria-labelledby="payment-type-label"
                  >
                    <div className={styles.segment}>
                      <input
                        type="radio"
                        id="payment-card"
                        name="paymentType"
                        checked={payment === "Card"}
                        onChange={() => setPayment("Card")}
                      />
                      <label className={styles.segmentLabel} htmlFor="payment-card">
                        <CreditCard size={16} aria-hidden="true" />
                        Card
                      </label>
                    </div>
                    <div className={styles.segment}>
                      <input
                        type="radio"
                        id="payment-cash"
                        name="paymentType"
                        checked={payment === "Cash"}
                        onChange={() => setPayment("Cash")}
                      />
                      <label className={styles.segmentLabel} htmlFor="payment-cash">
                        <Banknote size={16} aria-hidden="true" />
                        Cash
                      </label>
                    </div>
                  </div>
                </div>

                {payment === "Cash" && (
                  <div className={styles.cashRow}>
                    <div className={styles.field}>
                      <label className={styles.fieldLabel} htmlFor="cash-received">
                        Received
                      </label>
                      <input
                        id="cash-received"
                        className={`${styles.input} ${amountInvalid ? styles.inputInvalid : ""}`}
                        placeholder="0.00"
                        inputMode="decimal"
                        autoComplete="off"
                        aria-invalid={amountInvalid || undefined}
                        value={amountReceived}
                        onChange={(e) => setAmountReceived(e.target.value)}
                      />
                    </div>
                    <div className={styles.field}>
                      <label className={styles.fieldLabel} htmlFor="cash-change">
                        Change
                      </label>
                      <input
                        id="cash-change"
                        className={styles.input}
                        placeholder="0.00"
                        value={changeDue}
                        readOnly
                      />
                    </div>
                  </div>
                )}
              </>
            )}

            <SignaturePad
              receivedBy={receivedBy}
              onReceivedByChange={setReceivedBy}
              onChange={setSignature}
              disabled={completing}
            />

            {completeBlockedReason && (
              <div className={styles.statusMessage}>{completeBlockedReason}</div>
            )}

            <button
              className={styles.primaryBtn}
              disabled={completeDisabled}
              onClick={handleComplete}
              type="button"
            >
              <CheckCircle2 size={18} aria-hidden="true" />
              {completing ? "Completing…" : "Complete delivery"}
            </button>

            {/* The escape hatch: without it, a delivery that can't be made
                blocks every remaining stop on the route. */}
            <button
              className={styles.secondaryBtn}
              disabled={completing || failing}
              onClick={() => setShowFailureDialog(true)}
              type="button"
            >
              <AlertTriangle size={18} aria-hidden="true" />
              Can&apos;t deliver
            </button>

            <div className={styles.telemetry}>
              <span>GPS {gpsAccuracy != null ? `±${Math.round(gpsAccuracy)} m` : "—"}</span>
              {gpsSpeed != null && <span>{(gpsSpeed * 3.6).toFixed(0)} km/h</span>}
              <span>Start: {startLabel || "—"}</span>
            </div>
            </div>
          </>
        )}
      </div>

      {showFailureDialog && (
        <FailureDialog
          submitting={failing}
          onCancel={() => setShowFailureDialog(false)}
          onConfirm={handleFail}
        />
      )}
    </DriverScreen>
  )
}
